import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Container,
  Editable,
  EditableInput,
  EditablePreview,
  Heading,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Stack,
  Tag,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { createPrismaSort, DataTable, useCrud } from '@pelicargo/datatable';
import { RequestStatus, SelectOption } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  getPrettyRequestStatus,
  getPrettyShipmentStatus,
  modelUtils,
  prettyDate,
  prettyNumber,
} from '@pelicargo/utils';
import { formatAwb } from '@pelicargo/utils';
import { ShipmentStatus } from '@prisma/client';
import { createColumnHelper } from '@tanstack/react-table';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { HiChevronDown, HiOutlineMail, HiOutlineXCircle } from 'react-icons/hi';
import { HiCalendarDays } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import { CancelBookingModal } from '../../components/CancelBookingModal';
import { LabeledValue } from '../../components/LabeledValue';
import { Page } from '../../components/Page';
import { Select } from '../../components/Select';
import { UpdateTenderDateModal } from '../../components/UpdateTenderDateModal';
import { shipmentStatusColorScheme, statusColorScheme } from '../../config/theme';
import { useAccountAirports } from '../../hooks/airports/useAccountAirports';
import { useRequests } from '../../hooks/request/useRequests';
import { useUpdateCustomerRef } from '../../hooks/request/useUpdateCustomerRef';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import {
  formatCargoQuantity,
  formatDestinationAirports,
  formatOriginAirports,
  translateRequestStatusFromOptions,
} from '../../utils/helpers';

const PAGE_SIZE = 50;

const customerStatusOptions = [
  {
    label: 'In-Progress',
    value: 'InProgress',
  },
  {
    label: 'Booked',
    value: 'Booked',
  },
  {
    label: 'Cancelled',
    value: 'Cancelled',
  },
];

type ModalState = {
  openModal: 'changeTenderDate' | 'cancelBooking';
  requestId: number;
};

export const Requests = () => {
  const toast = useToast({ size: 'sm' });
  const { targetAccount } = useTargetAccount();

  const { query, handleQueryChange, sorting, setSorting, take, setPagination } = useCrud({ PAGE_SIZE });

  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [statuses, setStatuses] = useState([]);
  const [origins, setOrigins] = useState<string[]>();
  const [destinations, setDestinations] = useState<string[]>();
  const { data: airports, isFetching: isFetchingAirports } = useAccountAirports();
  const [updateCustomerRef, isUpdating] = useUpdateCustomerRef();

  const debouncedSearch = useDebounce<string>(query, 500);

  const { data, isFetching, isLoading, isRefetching, refetch } = useRequests({
    statuses,
    take,
    skip: 0,
    origins,
    destinations,
    search: debouncedSearch,
    orderBy: createPrismaSort(sorting),
  });

  useEffect(() => {
    refetch();
    // This should only run when the target account changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetAccount?.id]);

  const handleRequestStatusSelect = (values: SelectOption[]) => {
    setStatuses(translateRequestStatusFromOptions(values));
  };

  const handleOrigins = (options: SelectOption[]) => {
    setOrigins(options.map((o) => o.value));
  };

  const handleDestinations = (options: SelectOption[]) => {
    setDestinations(options.map((d) => d.value));
  };

  const updateRequest = useCallback(
    (requestId: number) => async (event: ChangeEvent<HTMLInputElement>) => {
      try {
        const value = event.target.value;
        await updateCustomerRef({ requestId, customerRef: value });

        toast({
          title: 'Reference number updated',
          status: 'success',
        });

        await refetch();
      } catch (error) {
        console.log(error);
        toast({
          title: error.message,
          status: 'error',
        });
      }
    },
    [refetch, toast, updateCustomerRef],
  );

  const handleLoadMore = () => {
    setPagination({ pageIndex: 0, pageSize: take + PAGE_SIZE });
  };
  const displayLoadMore = data?.requests?.length >= take;

  const originOptions = airports?.origins.map((node) => ({
    label: node,
    value: node,
  }));

  const destinationOptions = airports?.destinations.map((node) => ({
    label: node,
    value: node,
  }));

  const handleOpenModal = (modal: 'changeTenderDate' | 'cancelBooking', requestId: number) => () => {
    setModalState({ openModal: modal, requestId });
  };

  const handleCloseModal = () => setModalState(null);

  const supportEmailHelper = (requestId: number, origin: string, destination: string, customer_ref: string) => {
    const address = 'ship@pelicargo.com';
    const subject = `${origin} to ${destination} | R${requestId}` + (customer_ref ? ` | ${customer_ref}` : '');
    const mailtoStr = `mailto:${address}?subject=${subject}`;
    return mailtoStr;
  };

  const hasNonPendingShipments = useMemo(
    () =>
      data?.requests
        ?.map(({ shipment_status }) => shipment_status !== null && shipment_status !== ShipmentStatus.PENDING)
        .some(Boolean),
    [data?.requests],
  );

  const tableData = useMemo(() => {
    return data?.requests?.map((request) => ({
      id: request?.id,
      ref: `R${request?.id}`,
      toUnit: formatOriginAirports(request?.origin_airports),
      destination: formatDestinationAirports(request?.destination_airports),
      pieces: formatCargoQuantity(request?.cargo),
      gross: prettyNumber(request?.gross_weight),
      chargeable: prettyNumber(request?.chargeable_weight),
      status: request?.status,
      ...(hasNonPendingShipments ? { shipment_status: request?.shipment_status } : {}),
      awb: formatAwb(request?.booked_quote_variant?.quote?.awb_number),
      originalAwb: request?.booked_quote_variant?.quote?.original_customer_awb_number,
      tracking: request?.customer_ref,
      bookedQuoteVariantId: request?.booked_quote_variant?.quote_id, // This name is misleading since this is a quote id
      mainRequestId: request?.main_request_id,
      createdAt: request?.created_at,
      freight_services: request?.freight_services,
    }));
  }, [data?.requests, hasNonPendingShipments]);

  const columnHelper = createColumnHelper<any>();
  const columns = [
    columnHelper.accessor('id', {
      header: '',
      enableSorting: false,
      cell: (cell) => {
        const bookedQuoteVariantId = cell.row.original['bookedQuoteVariantId'];
        const requestId = cell.getValue();
        const status = cell.row.getValue('status');
        const isCancelled = status === RequestStatus.Cancelled || status === RequestStatus.BookingCancel;
        const mainRequestId = cell.row.original['mainRequestId'];
        const isDuplicate = !!mainRequestId;
        const isQuoteDisabled = isDuplicate;
        const isBooked = !!bookedQuoteVariantId;
        const isBookingDisabled = !isBooked || isCancelled || isDuplicate;
        const duplicateLabel = `This was marked as a duplicate of R${mainRequestId}`;
        let label = 'There are no booked quotes yet.';

        if (isCancelled) {
          label = 'This request has been cancelled.';
        } else if (isDuplicate) {
          label = duplicateLabel;
        }

        return (
          <ButtonGroup spacing={3}>
            <Tooltip isDisabled={!isQuoteDisabled} label={duplicateLabel}>
              <Box>
                <Button
                  as={Link}
                  size="sm"
                  colorScheme="primary"
                  isDisabled={isQuoteDisabled}
                  to={`/requests/${requestId}`}
                >
                  Quotes
                </Button>
              </Box>
            </Tooltip>
            <Tooltip isDisabled={!isBookingDisabled} label={label}>
              <Box>
                <Button
                  as={Link}
                  size="sm"
                  colorScheme="secondary"
                  isDisabled={isBookingDisabled}
                  to={`/requests/${requestId}/quotes/${bookedQuoteVariantId}/booked`}
                >
                  Booking
                </Button>
              </Box>
            </Tooltip>
            <Menu isLazy>
              <MenuButton
                as={Button}
                colorScheme="gray"
                size="sm"
                isDisabled={isQuoteDisabled || isBookingDisabled}
                rightIcon={<HiChevronDown />}
              >
                Menu
              </MenuButton>
              <Portal>
                <MenuList>
                  <MenuItem
                    icon={<HiOutlineMail />}
                    as={Link}
                    to={supportEmailHelper(
                      requestId,
                      cell.row.original.toUnit,
                      cell.row.original.destination,
                      cell.row.original.tracking,
                    )}
                  >
                    Help with Request
                  </MenuItem>
                  <MenuItem
                    icon={<HiCalendarDays />}
                    onClick={handleOpenModal('changeTenderDate', requestId as number)}
                  >
                    Change Tender Date
                  </MenuItem>
                  <MenuItem icon={<HiOutlineXCircle />} onClick={handleOpenModal('cancelBooking', requestId as number)}>
                    Cancel Booking
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </ButtonGroup>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const status = info.getValue();

        return (
          <Tag w="fit-content" colorScheme={statusColorScheme[status]}>
            {getPrettyRequestStatus(status, true)}
          </Tag>
        );
      },
    }),
    ...(hasNonPendingShipments
      ? [
          columnHelper.accessor('shipment_status', {
            header: 'Shipment Status',
            cell: (info) => {
              const status = info.getValue();
              const isAfrOnly = modelUtils.request.isAfrOnly(info.row.original);

              if (isAfrOnly) {
                return (
                  <Tag w="fit-content" colorScheme={'gray'}>
                    -
                  </Tag>
                );
              }

              return (
                <Tag w="fit-content" colorScheme={shipmentStatusColorScheme[status]}>
                  {status === ShipmentStatus.PENDING ? '-' : getPrettyShipmentStatus(status)}
                </Tag>
              );
            },
          }),
        ]
      : []),
    columnHelper.accessor('ref', {
      header: 'Request #',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created at',
      cell: (info) =>
        prettyDate(info.getValue(), {
          showTime: true,
        }),
    }),
    columnHelper.accessor('toUnit', {
      header: 'Origin',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('destination', {
      header: 'Dest',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('pieces', {
      header: '# Pieces',
      enableSorting: false, // See getRequests procedure
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('chargeable', {
      header: 'Chargeable (kg)',
      cell: (info) => info.getValue(),
    }),

    columnHelper.accessor('awb', {
      header: 'AWB #',
      cell: useCallback((info) => {
        const awb = info.getValue();
        const originalAwb = info.row.original.originalAwb;
        const cleanedOriginalAWB = originalAwb?.replace(/[^0-9]/g, '');
        const cleanedAWB = awb?.replace(/[^0-9]/g, '');
        const doBothAWBExist = !!cleanedOriginalAWB && !!cleanedAWB;
        const isAwbUpdated = cleanedOriginalAWB !== cleanedAWB && doBothAWBExist;

        const value = isAwbUpdated ? (
          <LabeledValue
            label={awb}
            value={
              <Tag colorScheme="blue" size="sm">
                Updated MAWB #
              </Tag>
            }
          />
        ) : (
          awb
        );

        return value;
      }, []),
    }),
    columnHelper.accessor('tracking', {
      header: 'Reference #',
      cell: useCallback(
        (info) => {
          const allCells = info?.row?.getAllCells();
          const value = info.getValue();
          const index = info.row.index;
          const idRef = `${index}_id`;

          const id = allCells.find((n) => n?.id === idRef)?.getValue();

          return (
            <Editable defaultValue={value} isDisabled={isUpdating} w="full">
              <EditablePreview w="full" />
              <EditableInput w="full" onBlur={updateRequest(Number(id))} />
            </Editable>
          );
        },
        [isUpdating, updateRequest],
      ),
    }),
  ];

  const isLoadingRequests = isFetching || isLoading || isRefetching || isFetchingAirports || isUpdating;

  return (
    <Page title="Requests" flex="1">
      <Container w="full" maxW="full" px={{ base: '4', lg: '16' }} py="10">
        <Stack minH="full" w="full" spacing="6">
          <Heading variant="h3">Requests</Heading>
          <Stack direction={{ base: 'column', md: 'row' }} w="full" spacing="6">
            <HStack w="full">
              <Input
                placeholder="Search"
                size="lg"
                minH={{ base: '60px', md: 'auto' }}
                h={{ base: undefined, md: 'full' }}
                borderRadius="lg"
                bgColor="bg.surface"
                borderColor="border.default"
                onChange={handleQueryChange}
              />
            </HStack>

            <HStack w="full">
              <Select label="Origin" options={originOptions} onChange={handleOrigins} />
            </HStack>
            <HStack w="full">
              <Select label="Destination" options={destinationOptions} onChange={handleDestinations} />
            </HStack>
            <HStack w="full">
              <Select label="Status" options={customerStatusOptions} onChange={handleRequestStatusSelect} />
            </HStack>
          </Stack>
          <Show if={data?.requests?.length > 0}>
            <DataTable
              columns={columns}
              data={tableData}
              dataCount={data?.requests?.length}
              pagination={{
                pageIndex: 0,
                pageSize: data?.requests?.length ?? 0,
              }}
              setPagination={() => true}
              hidePagination
              sorting={sorting}
              setSorting={setSorting}
              variant="lg"
            />
          </Show>
          <Show if={isLoadingRequests}>
            <Center flex={1} py={16}>
              <Spinner />
            </Center>
          </Show>
          <Show if={displayLoadMore}>
            <Stack alignItems="center">
              <Button onClick={handleLoadMore}>Load More</Button>
            </Stack>
          </Show>
        </Stack>
      </Container>
      <Show if={modalState?.openModal === 'changeTenderDate'}>
        <UpdateTenderDateModal
          requestId={modalState?.requestId}
          refetch={refetch}
          isOpen={modalState?.openModal === 'changeTenderDate'}
          onClose={handleCloseModal}
        />
      </Show>
      <Show if={modalState?.openModal === 'cancelBooking'}>
        <CancelBookingModal
          requestId={modalState?.requestId}
          refetch={refetch}
          isOpen={modalState?.openModal === 'cancelBooking'}
          onClose={handleCloseModal}
        />
      </Show>
    </Page>
  );
};
